import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Household } from '../../../../common/src/model.ts';
import { OnboardingPages } from '../../../../common/src/pages.ts';
import * as server_api from '../../../../common/src/server_api.ts';
import { isValidEmail } from '../../../../common/src/util.ts';
import { localStorageGet, LocalStorageKey, localStorageSet } from '../../data/client_local_storage.ts';
import { getHouseholdFromServer } from '../../data/use_household.tsx';
import { useLocalStorage } from '../../data/use_local_storage.tsx';
import { alert } from '../../util/confirm.tsx';
import { Button } from './button.tsx';
import { goToNextOnboardingStep, Message, TextInput } from './common.tsx';
import { Frame } from './frame.tsx';

export const HouseholdEmail = () => {
  const emailAddressInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    emailAddressInputRef.current?.focus();
  }, [emailAddressInputRef]);

  const [householdEmail, setHouseholdEmail] = useLocalStorage(LocalStorageKey.OnboardingHouseholdEmail);
  const onEmailChange = useCallback(() => {
    setHouseholdEmail(emailAddressInputRef.current?.value);
  }, [setHouseholdEmail]);

  const onContinue = useCallback(async () => {
    const householdEmail = emailAddressInputRef.current?.value;
    const familyLastName = localStorageGet(LocalStorageKey.OnboardingLastName);
    if (!householdEmail || !isValidEmail(householdEmail)) {
      await alert({ confirmation: <span>Please enter a valid email address</span> });
      return false;
    }
    if (!familyLastName) {
      await alert({ confirmation: <span>Please enter your last name</span> });
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdEmail, action: 'lastNameMissing' });
      return;
    }
    let household: Household | undefined = undefined;
    try {
      const householdToken = await server_api.getHouseholdToken({ householdEmail });
      if (householdToken) {
        household = await getHouseholdFromServer(householdToken);
      }
    } catch {
      await alert({
        confirmation: (
          <span>
            Network error. Please make sure that you are connected to the Internet. If this problem persists, contact
            your internet service provider.
          </span>
        ),
      });
      return;
    }
    if (household) {
      localStorageSet(LocalStorageKey.OnboardingMatchingHousehold, household);
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdEmail, action: 'householdFound' });
    } else {
      await goToNextOnboardingStep(navigate, { from: OnboardingPages.HouseholdEmail, action: 'householdNotFound' });
    }
  }, [navigate]);

  return (
    <Frame
      onboardingPage={OnboardingPages.HouseholdEmail}
      mainContent={
        <>
          <Message $widthInChars={16}>What is a good email for the family?</Message>
          <TextInput
            type="email"
            enterKeyHint="next"
            defaultValue={householdEmail}
            onChange={onEmailChange}
            ref={emailAddressInputRef}
          />
        </>
      }
      footerContent={
        <Button onClick={onContinue} disabled={!isValidEmail(householdEmail)}>
          CONTINUE
        </Button>
      }
      onEnterKeypress={onContinue}
      containsInputElement={true}
    />
  );
};
